import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, Edit2, Trash2, GripVertical } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import backIcon from '../../../Images/addstarsbackIcon.png';
import '../../Dashboard/Darts/masterConfiguration/productConfiguration/statuses.css';
import '../../Dashboard/Darts/masterConfiguration/productConfiguration/stages.css';
import Editicon from "../../../Images/editstages.png";
import { ChromePicker } from 'react-color';
import { useQuery, useMutation } from '@apollo/client';
import { GET_ALL_STATUS_FOR_JOBROLE, ADD_UPDATE_STATUS, DELETE_STATUS, REORDER_STATUSES } from '../../../services/userAPI';
import { message } from 'antd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PageBreadcrumb from '../../../reusableField/PageBreadcrumb';

const AdminStatuses = () => {
    const [statuses, setStatuses] = useState([]);
    const [originalStatuses, setOriginalStatuses] = useState([]);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [selectedStatusId, setSelectedStatusId] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newStatusName, setNewStatusName] = useState('');
    const [newStatusColor, setNewStatusColor] = useState('#000000');
    const [nameError, setNameError] = useState('');
    const [colorError, setColorError] = useState('');
    const [editedStatusId, setEditedStatusId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [statusToDelete, setStatusToDelete] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isReordering, setIsReordering] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const inputRef = useRef(null);

    const location = useLocation();
    const navigate = useNavigate();
    const jobroleId = location.state?.roleId || 1;

    const { data, loading, error } = useQuery(GET_ALL_STATUS_FOR_JOBROLE, {
        variables: { jobroleId },
        onCompleted: (data) => {
            if (data.getAllStatusForJobrole) {
                const formattedStatuses = data.getAllStatusForJobrole.map(status => ({
                    id: status.id,
                    name: status.statusName,
                    color: `#${status.statusColor.substring(2)}`
                }));
                setStatuses(formattedStatuses);
                setOriginalStatuses([...formattedStatuses]);
            }
        },
    });

    const [addUpdateStatus] = useMutation(ADD_UPDATE_STATUS);
    const [deleteStatus] = useMutation(DELETE_STATUS);
    const [reorderStatuses] = useMutation(REORDER_STATUSES);

    const dartsFormNeeded = location.state?.dartsFormNeeded;
    const accountId = location.state?.accountId;
    const roleId = location.state?.roleId;
    const pocName = location.state?.pocName;
    const pocEmail = location.state?.pocEmail;
    const pocNumber = location.state?.pocNumber;
    const numberLogin = location.state?.numberLogin;
    const emailLogin = location.state?.emailLogin;
    const mandatoryTimeDistribution = location.state?.mandatoryTimeDistribution;
    const targetEditDays = location.state?.targetEditDays;
    const inputEditDays = location.state?.inputEditDays;
    const name = location.state?.name;
    const dartsNeeded = location.state?.dartsNeeded;
    const clanNeeded = location.state?.clanNeeded;
    const syncNeeded = location.state?.syncNeeded;
    const accountName = location.state?.accountName;

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [editingId, showAddModal]);

    const handleAddStatus = () => {
        setShowAddModal(true);
        setEditedStatusId(null);
        setNewStatusName('');
        setNewStatusColor('#000000');
        setNameError('');
        setColorError('');
    };

    const handleSaveStatus = async () => {
        let hasError = false;

        if (!newStatusName.trim()) {
            setNameError('Status name is required.');
            hasError = true;
        } else {
            setNameError('');
        }

        if (!newStatusColor || newStatusColor === '#000000') {
            setColorError('Please select a valid color.');
            hasError = true;
        } else {
            setColorError('');
        }

        if (!hasError) {
            setIsSaving(true);
            const colorCode = newStatusColor.replace('#', '');
            try {
                const { data } = await addUpdateStatus({
                    variables: {
                        jobroleId,
                        statusName: newStatusName.trim(),
                        statusId: editedStatusId,
                        colorCode
                    }
                });
                if (data.addUpdateStatus.success) {
                    if (editedStatusId) {
                        setStatuses(statuses.map(status =>
                            status.id === editedStatusId ? { ...status, name: newStatusName.trim(), color: newStatusColor } : status
                        ));
                        message.success('Status updated successfully!');
                    } else {
                        const newStatus = {
                            id: data.addUpdateStatus.data.id,
                            name: newStatusName.trim(),
                            color: newStatusColor
                        };
                        setStatuses([...statuses, newStatus]);
                        message.success('Status added successfully!');
                    }
                    setNewStatusName('');
                    setNewStatusColor('#000000');
                    setShowAddModal(false);
                } else {
                    setNameError(data.addUpdateStatus.message || 'Failed to save status.');
                    message.error(data.addUpdateStatus.message || 'Failed to save status.');
                }
            } catch (error) {
                setNameError('An error occurred while saving the status.');
                message.error('An error occurred while saving the status.');
                console.error(error);
            } finally {
                setIsSaving(false);
            }
        }
    };

    const handleEditStatus = (id) => {
        const status = statuses.find(s => s.id === id);
        if (status) {
            setShowAddModal(true);
            setEditedStatusId(id);
            setNewStatusName(status.name);
            setNewStatusColor(status.color);
            setNameError('');
            setColorError('');
        }
    };

    const handleSaveEdit = async () => {
        if (editingName.trim()) {
            try {
                const status = statuses.find(s => s.id === editingId);
                const colorCode = status.color.replace('#', '');
                const { data } = await addUpdateStatus({
                    variables: {
                        jobroleId,
                        statusName: editingName.trim(),
                        statusId: editingId,
                        colorCode
                    }
                });
                if (data.addUpdateStatus.success) {
                    setStatuses(statuses.map(status =>
                        status.id === editingId ? { ...status, name: editingName.trim() } : status
                    ));
                    setEditingId(null);
                    setEditingName('');
                    message.success('Status updated successfully!');
                } else {
                    setNameError(data.addUpdateStatus.message || 'Failed to update status.');
                    message.error(data.addUpdateStatus.message || 'Failed to update status.');
                }
            } catch (error) {
                setNameError('An error occurred while updating the status.');
                message.error('An error occurred while updating the status.');
            }
        }
    };

    const handleDeleteStatus = (id) => {
        const status = statuses.find(s => s.id === id);
        if (status) {
            setStatusToDelete(status);
            setShowDeleteModal(true);
        }
    };

    const confirmDelete = async () => {
        if (statusToDelete) {
            setIsDeleting(true);
            try {
                const { data } = await deleteStatus({
                    variables: { statusId: statusToDelete.id }
                });
                if (data.deleteStatus.success) {
                    setStatuses(statuses.filter(status => status.id !== statusToDelete.id));
                    message.success('Status deleted successfully!');
                } else {
                    setNameError(data.deleteStatus.message || 'Failed to delete status.');
                    message.error(data.deleteStatus.message || 'Failed to delete status.');
                }
            } catch (error) {
                setNameError('An error occurred while deleting the status.');
                message.error('An error occurred while deleting the status.');
            } finally {
                setIsDeleting(false);
                setShowDeleteModal(false);
                setStatusToDelete(null);
            }
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setStatusToDelete(null);
    };

    const handleKeyDown = (e, action) => {
        if (e.key === 'Enter') {
            action();
        } else if (e.key === 'Escape') {
            if (editingId) {
                handleCancelEdit();
            } else if (showAddModal) {
                setShowAddModal(false);
                setNameError('');
                setColorError('');
            }
        }
    };

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (draggedIndex !== null && draggedIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();

        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const newStatuses = [...statuses];
        const draggedItem = newStatuses[draggedIndex];
        newStatuses.splice(draggedIndex, 1);
        newStatuses.splice(dropIndex, 0, draggedItem);

        setStatuses(newStatuses);
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditingName('');
    };

    const handleBack = () => {
        navigate("/admin-home");
    };

    const handleBackToViewRole = () => {
        const safeRoleId = roleId || location.state?.roleId;
        const safeAccountName = accountName || location.state?.accountName;

        navigate("/admin-view-role", {
            state: {
                roleId: safeRoleId,
                accountId: accountId || location.state?.accountId,
                accountName: safeAccountName,
                name: name || location.state?.name,
                dartsNeeded: dartsNeeded || location.state?.dartsNeeded,
                clanNeeded: clanNeeded || location.state?.clanNeeded,
                syncNeeded: syncNeeded || location.state?.syncNeeded,
                dartsFormNeeded: dartsFormNeeded || location.state?.dartsFormNeeded,
                pocName: pocName || location.state?.pocName,
                pocEmail: pocEmail || location.state?.pocEmail,
                pocNumber: pocNumber || location.state?.pocNumber,
                numberLogin: numberLogin || location.state?.numberLogin,
                emailLogin: emailLogin || location.state?.emailLogin,
                mandatoryTimeDistribution: mandatoryTimeDistribution || location.state?.mandatoryTimeDistribution,
                targetEditDays: targetEditDays || location.state?.targetEditDays,
                inputEditDays: inputEditDays || location.state?.inputEditDays,
            },
        });
    };

    const handleDiscardClick = () => {
        navigate("/admin-home");
    };

    const handleReorder = async () => {
        setIsReordering(true);
        try {
            const reorderIds = statuses.map(status => status.id);
            const { data } = await reorderStatuses({
                variables: { jobroleId, reorderIds }
            });
            if (data.reorderStatus.success) {
                setOriginalStatuses([...statuses]);
                message.success('Statuses reordered successfully!');
            } else {
                setNameError(data.reorderStatus.message || 'Failed to reorder statuses.');
                setStatuses([...originalStatuses]);
                message.error(data.reorderStatus.message || 'Failed to reorder statuses.');
            }
        } catch (error) {
            setNameError('An error occurred while reordering statuses.');
            setStatuses([...originalStatuses]);
            message.error('An error occurred while reordering statuses.');
            console.error('Reorder error details:', error.message, error.stack);
        } finally {
            setIsReordering(false);
        }
    };

    const handleReset = () => {
        setStatuses([...originalStatuses]);
        message.success('Statuses reset to original order!');
    };

    const handleColorClick = (id) => {
        setSelectedStatusId(id);
        setShowColorPicker(true);
    };

    const handleColorChange = (color) => {
        if (selectedStatusId !== null) {
            setStatuses(statuses.map(status =>
                status.id === selectedStatusId ? { ...status, color: color.hex } : status
            ));
        } else if (showAddModal) {
            setNewStatusColor(color.hex);
            setColorError('');
        }
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error.message}</div>;

    const filteredStatuses = statuses.filter(status =>
        status.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="user-management-container">
            <PageBreadcrumb homePath="/admin">
                <span onClick={handleBack} style={{ cursor: 'pointer' }}>{accountName}</span>
                <span>&gt;</span>
                <span onClick={() => handleBackToViewRole({ state: { name, accountName, roleId, dartsFormNeeded, dartsNeeded } })} style={{ cursor: 'pointer' }}>{name}</span>
                <span>&gt;</span>
                <span style={{ fontWeight: 'bold' }}>Darts</span>
            </PageBreadcrumb>

            <div>
                <div className="tabs-container-stages">
                    <span className="config-label" onClick={() => navigate('/admin-product-configuration', { state: { name, accountName, roleId, dartsFormNeeded, dartsNeeded, roleId, accountId, clanNeeded, syncNeeded } })}>
                        <span>LDMS Configuration</span>
                    </span>
                    <span className="tabs-stages">
                        <button
                            className="tab-btn"
                            onClick={() => navigate('/admin-activities', { state: { name, accountName, roleId, dartsFormNeeded, dartsNeeded, roleId, accountId, clanNeeded, syncNeeded } })}
                        >
                            Activities
                        </button>
                        <button
                            className="tab-btn active"
                        >
                            Statuses
                        </button>
                        <button
                            className="tab-btn"
                            onClick={() => navigate('/admin-stages', { state: { name, accountName, roleId, dartsFormNeeded, dartsNeeded, roleId, accountId, clanNeeded, syncNeeded } })}
                        >
                            Stages
                        </button>
                        <button
                            className="tab-btn"
                            onClick={() => navigate('/forms-configuration', { state: { name, accountName, roleId, dartsFormNeeded, dartsNeeded, roleId, accountId, clanNeeded, syncNeeded } })}
                        >
                            Forms Configuration
                        </button>
                    </span>
                </div>

                <div style={{
                    borderRadius: '4px',
                    marginBottom: '16px',
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center'
                }}>
                    <input
                        type="text"
                        placeholder="Search status name"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            flex: 1,
                            padding: '10px 12px',
                            border: '1px solid #a3a3a3',
                            borderRadius: '4px',
                            fontSize: '14px',
                            outline: 'none'
                        }}
                    />
                    <button
                        onClick={handleAddStatus}
                        style={{
                            padding: '10px 24px',
                            backgroundColor: '#AD0101',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 500
                        }}
                    >
                        Add Status
                    </button>
                </div>

                {filteredStatuses.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '16px',
                        fontFamily: 'Inter',
                        color: '#666'
                    }}>
                        No statuses found.
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {filteredStatuses.map((status, index) => (
                            <div
                                key={status.id}
                                draggable={editingId !== status.id}
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, index)}
                                onDragEnd={handleDragEnd}
                                style={{
                                    backgroundColor: 'white',
                                    padding: '16px',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    border: dragOverIndex === index ? '2px solid #1890ff' : '1px solid #e8e8e8',
                                    opacity: draggedIndex === index ? 0.5 : 1,
                                    cursor: editingId === status.id ? 'default' : 'move',
                                    transition: 'all 0.2s ease',
                                    transform: dragOverIndex === index ? 'scale(1.02)' : 'scale(1)',
                                    boxShadow: dragOverIndex === index ? '0 4px 12px rgba(0,0,0,0.1)' : 'none'
                                }}
                            >
                                <DragIndicatorIcon
                                    size={20}
                                    color="#000000ff"
                                    style={{
                                        cursor: editingId === status.id ? 'default' : 'grab',
                                        flexShrink: 0
                                    }}
                                />

                                {editingId === status.id ? (
                                    <>
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            value={editingName}
                                            onChange={(e) => setEditingName(e.target.value)}
                                            onKeyDown={(e) => handleKeyDown(e, handleSaveEdit)}
                                            style={{
                                                flex: 1,
                                                padding: '8px 12px',
                                                border: '1px solid #d9d9d9',
                                                borderRadius: '4px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                        />
                                        <button
                                            onClick={handleSaveEdit}
                                            style={{
                                                padding: '6px 16px',
                                                backgroundColor: '#AD0101',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '14px'
                                            }}
                                        >
                                            Save
                                        </button>
                                        <button
                                            onClick={handleCancelEdit}
                                            style={{
                                                padding: '6px 16px',
                                                backgroundColor: '#d9d9d9',
                                                color: '#333',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '14px'
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            flex: 1
                                        }}>
                                            <div
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    borderRadius: '50%',
                                                    backgroundColor: status.color,
                                                }}
                                                onClick={() => handleColorClick(status.id)}
                                            />
                                            <span style={{
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                fontFamily: 'Inter',
                                            }}>
                                                {status.name}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => handleEditStatus(status.id)}
                                            style={{
                                                padding: '6px',
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <img src={Editicon} alt="Edit" style={{ width: '18px', height: '18px' }} />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteStatus(status.id)}
                                            style={{
                                                padding: '6px',
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Trash2 size={18} color="#AD0101" />
                                        </button>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {showAddModal && (
                    <div style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1001,
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        width: '300px',
                        textAlign: 'center'
                    }}>
                        <h3 style={{
                            fontFamily: 'Inter',
                            fontSize: '18px',
                            fontWeight: '600',
                            marginBottom: '15px',
                            color: '#333'
                        }}>{editedStatusId ? 'Edit Status' : 'Add New Status'}</h3>
                        <div style={{ textAlign: 'left', marginBottom: '15px' }}>
                            <label style={{
                                fontFamily: 'Inter',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#333'
                            }}>
                                Status Name <span style={{ color: '#AD0101' }}>*</span>
                            </label>
                            <input
                                ref={inputRef}
                                type="text"
                                placeholder="Enter status name"
                                value={newStatusName}
                                onChange={(e) => {
                                    setNewStatusName(e.target.value);
                                    const searchInput = document.querySelector('input[type="text"]:first-of-type');
                                    if (searchInput) searchInput.value = e.target.value;
                                }}
                                onKeyDown={(e) => handleKeyDown(e, handleSaveStatus)}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    marginTop: '5px',
                                    border: '1px solid #a3a3a3',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    outline: 'none'
                                }}
                            />
                            {nameError && (
                                <p style={{
                                    color: '#AD0101',
                                    fontSize: '12px',
                                    marginTop: '5px',
                                    textAlign: 'left'
                                }}>{nameError}</p>
                            )}
                        </div>
                        <div style={{ textAlign: 'left', marginBottom: '20px' }}>
                            <label style={{
                                fontFamily: 'Inter',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#333'
                            }}>
                                Color <span style={{ color: '#AD0101' }}>*</span>
                            </label>
                            <ChromePicker
                                color={newStatusColor}
                                onChange={handleColorChange}
                                disableAlpha={true}
                                width="200px"
                                style={{ marginTop: '5px' }}
                            />
                            {colorError && (
                                <p style={{
                                    color: '#AD0101',
                                    fontSize: '12px',
                                    marginTop: '5px',
                                    textAlign: 'left'
                                }}>{colorError}</p>
                            )}
                        </div>
                        <div>
                            <button
                                onClick={handleSaveStatus}
                                disabled={isSaving}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: isSaving ? '#a3a3a3' : '#AD0101',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: isSaving ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    marginRight: '10px'
                                }}
                            >
                                {isSaving ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : (editedStatusId ? 'Update' : 'Save')}
                            </button>
                            <button
                                onClick={() => {
                                    setShowAddModal(false);
                                    setNameError('');
                                    setColorError('');
                                }}
                                disabled={isSaving}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#d9d9d9',
                                    color: '#333',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: isSaving ? 'not-allowed' : 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {showDeleteModal && statusToDelete && (
                    <div style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1001,
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        width: '300px',
                        textAlign: 'center'
                    }}>
                        <h3 style={{
                            fontFamily: 'Inter',
                            fontSize: '18px',
                            fontWeight: '600',
                            marginBottom: '15px',
                            color: '#333'
                        }}>Confirm Delete</h3>
                        <p style={{
                            fontFamily: 'Inter',
                            fontSize: '14px',
                            color: '#333',
                            marginBottom: '20px'
                        }}>
                            Do you want to delete this '{statusToDelete.name}'?
                        </p>
                        <div>
                            <button
                                onClick={confirmDelete}
                                disabled={isDeleting}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#AD0101',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    marginRight: '10px',
                                    opacity: isDeleting ? 0.7 : 1
                                }}
                            >
                                {isDeleting ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Yes'}
                            </button>
                            <button
                                onClick={cancelDelete}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#d9d9d9',
                                    color: '#333',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                No
                            </button>
                        </div>
                    </div>
                )}

                {(showAddModal || showDeleteModal) && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 1000
                        }}
                        onClick={() => {
                            if (showAddModal) {
                                setShowAddModal(false);
                                setNameError('');
                                setColorError('');
                            }
                            if (showDeleteModal) {
                                setShowDeleteModal(false);
                                setStatusToDelete(null);
                            }
                        }}
                    />
                )}

                {filteredStatuses.length > 0 && (
                    <div style={{
                        marginTop: '24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <a href="#" style={{
                            color: '#1890ff',
                            textDecoration: 'none',
                            fontSize: '14px'
                        }}>
                        </a>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={handleReset}
                                style={{
                                    padding: '8px 24px',
                                    backgroundColor: 'white',
                                    color: '#333',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                Reset
                            </button>
                            <button
                                onClick={handleReorder}
                                disabled={isReordering}
                                style={{
                                    padding: '8px 24px',
                                    backgroundColor: '#AD0101',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: isReordering ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    opacity: isReordering ? 0.7 : 1
                                }}
                            >
                                {isReordering ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Reorder'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminStatuses;